import * as Components from '../components'
import * as Hooks from '../hooks'
import * as Navigation from '../navigation'
import * as Stores from '../stores'
import { View } from 'tamagui'
import { useController, useForm } from 'react-hook-form'
import { router } from 'expo-router'
import { AutoCompleteAddress } from '@/src/common/components/AutoCompleteAddress'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'

type AddressEntryFormValues = Parameters<
  ReturnType<typeof Hooks.useAddressValidator>['addressValidator']
>[0]

const DefaultValues = {
  primaryAddress: {
    street1: '',
    city: '',
    state: '',
    zipCode: '',
  },
  mailingAddress: undefined,
  isMailingAddressDifferent: false,
} satisfies AddressEntryFormValues

const containsValues = (values: AddressEntryFormValues) =>
  values.primaryAddress.street1 &&
  values.primaryAddress.city &&
  values.primaryAddress.state &&
  values.primaryAddress.zipCode

const isEmptyAddress: (
  address: AddressEntryFormValues['primaryAddress'] | AddressEntryFormValues['mailingAddress'],
) => boolean = (address) =>
  !(address?.street1 || address?.street2 || address?.city || address?.state || address?.zipCode)

export function AddressEntryScreen() {
  const Alert = useAlert(AddressEntryScreen.name)
  const { addressValidator: addressResolver, saveUserAddress } = Hooks.useAddressValidator()
  const updateField = Stores.useCardApplicationFormStore((s) => s.updateField)
  const cardApplicationForm = Stores.useCardApplicationFormStore((s) => s.cardApplicationForm)
  const defaultValues = containsValues(cardApplicationForm) ? cardApplicationForm : DefaultValues

  const form = useForm<AddressEntryFormValues>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: addressResolver,
  })
  const primaryAddressControl = useController({ control: form.control, name: 'primaryAddress' })
  const mailingAddressControl = useController({ control: form.control, name: 'mailingAddress' })
  const mailingAddressDifferentControl = useController({
    control: form.control,
    name: 'isMailingAddressDifferent',
  })

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await saveUserAddress(data)
      updateField('primaryAddress', data.primaryAddress)
      updateField('mailingAddress', data.mailingAddress)
      router.push(Navigation.Routes.LastQuestion.route)
    } catch (error) {
      Alert.alert(
        'Error',
        error instanceof Error
          ? error.message
          : "We weren't able to validate your primary address at this time, please try again",
      )
    }
  })

  return (
    <View flex={1} bg="$white1" pb="$8">
      <Components.ApplicationScreen>
        <Components.ApplicationScreen.Header previousRoute={Navigation.Routes.OfferCode} />
        <Components.ApplicationScreen.Content title="What's your primary address?">
          <Components.FormHelpText>
            We'll ship your card to your mailing address.
          </Components.FormHelpText>
          {/* TODO: Migrate AutoCompleteAddress to Tamagui, maybe? */}
          <AutoCompleteAddress
            mode="card-application-address"
            address={
              isEmptyAddress(primaryAddressControl.field.value)
                ? undefined
                : {
                    street2: '',
                    ...primaryAddressControl.field.value,
                  }
            }
            onChange={(address) => {
              primaryAddressControl.field.onChange(address)
            }}
            style={{ flexBasis: 'auto', flexShrink: 0, paddingTop: -888, paddingBottom: -888 }}
            showErrors={form.formState.submitCount > 0}
            sardineIds={{
              autoComplete: 'autocomplete',
              fullAddress: {
                street1: 'street1',
                street2: 'street2',
                city: 'city',
                state: 'state',
                zipCode: 'zip',
              },
            }}
          />
          {primaryAddressControl.fieldState.error && (
            <Components.FormHelpText color="$red10">
              {primaryAddressControl.fieldState.error.message}
            </Components.FormHelpText>
          )}
          <Components.FormHelpText>
            Currently, we are only available in the United States. PO Box, PMB, business, temporary,
            and non-US based addresses are not accepted.
          </Components.FormHelpText>
          <Components.FormToggle
            label="My mailing address is different"
            value={mailingAddressDifferentControl.field.value}
            onChange={(value) => mailingAddressDifferentControl.field.onChange(value)}
          />
          {mailingAddressDifferentControl.field.value && (
            <>
              <AutoCompleteAddress
                mode="card-physical-ship-address"
                address={
                  isEmptyAddress(mailingAddressControl.field.value) ||
                  !mailingAddressControl.field.value
                    ? undefined
                    : {
                        street2: '',
                        ...mailingAddressControl.field.value,
                      }
                }
                onChange={(address) => {
                  mailingAddressControl.field.onChange(address)
                }}
                style={{ flexBasis: 'auto', flexShrink: 0, paddingTop: -888, paddingBottom: -888 }}
                showErrors={form.formState.submitCount > 0}
              />
              {mailingAddressControl.fieldState.error && (
                <Components.FormHelpText color="$red10">
                  {mailingAddressControl.fieldState.error.message}
                </Components.FormHelpText>
              )}
            </>
          )}
        </Components.ApplicationScreen.Content>
        <Components.ApplicationScreen.SubmitButton
          onPress={() => void onSubmit()}
          loading={form.formState.isSubmitting}
          title="Continue"
        />
      </Components.ApplicationScreen>
    </View>
  )
}
